/**
 * StreamingMarkdown Component
 * Reveals markdown content progressively (like ChatGPT/Claude typing effect)
 * Only used for new AI messages, not for reloaded conversation history
 */

import React, { useState, useEffect, useRef } from 'react';
import Markdown from 'react-native-markdown-display';

interface StreamingMarkdownProps {
  content: string;
  markdownStyles: any;
  onLinkPress?: (url: string) => any;
  onStreamingComplete?: () => void;
  /** Characters revealed per tick */
  charsPerTick?: number;
  /** Milliseconds between ticks */
  tickInterval?: number;
}

export default function StreamingMarkdown({
  content,
  markdownStyles,
  onLinkPress,
  onStreamingComplete,
  charsPerTick = 5,
  tickInterval = 18,
}: StreamingMarkdownProps) {
  const [visibleLength, setVisibleLength] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completedRef = useRef(false);

  useEffect(() => {
    setVisibleLength(0);
    completedRef.current = false;

    intervalRef.current = setInterval(() => {
      setVisibleLength((prev) => {
        const next = prev + charsPerTick;
        if (next >= content.length) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          if (!completedRef.current) {
            completedRef.current = true;
            onStreamingComplete?.();
          }
          return content.length;
        }
        return next;
      });
    }, tickInterval);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [content]);

  const visibleContent = content.slice(0, visibleLength);

  return (
    <Markdown style={markdownStyles} onLinkPress={onLinkPress}>
      {visibleContent}
    </Markdown>
  );
}
